import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const HeatTreatReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const heatMeasurements = useMemo(() => measurements.filter((measurement) => measurement.reportType === 'HEAT_TREAT'), [measurements]);
  const [activeCycleId, setActiveCycleId] = useState<string | null>(null);
  useEffect(() => {
    if (!heatMeasurements.length) {
      setActiveCycleId(null);
      return;
    }

    if (!activeCycleId || !heatMeasurements.some((measurement) => measurement.id === activeCycleId)) {
      setActiveCycleId(heatMeasurements[0].id);
    }
  }, [activeCycleId, heatMeasurements]);
  const activeCycle = heatMeasurements.find((measurement) => measurement.id === activeCycleId) || heatMeasurements[0];
  const effectiveFrequency = metadata?.frequency || frequency || 'per_batch';
  const batchId = activeCycle?.heatBatchId || '-';
  const pipesInBatch = Number(activeCycle?.heatPipesInBatch || 0);
  const ausTempMin = getProjectLimitMin(projectLimitFields, 'austenitising_temp_c', 900);
  const ausTempMax = getProjectLimitMax(projectLimitFields, 'austenitising_temp_c', 940);
  const ausHoldMin = getProjectLimitMin(projectLimitFields, 'austenitising_hold_min', 25);
  const coolingRateMin = getProjectLimitMin(projectLimitFields, 'cooling_rate_cps', 18);
  const temperTempMin = getProjectLimitMin(projectLimitFields, 'tempering_temp_c', 580);
  const temperTempMax = getProjectLimitMax(projectLimitFields, 'tempering_temp_c', 640);
  const temperHoldMin = getProjectLimitMin(projectLimitFields, 'tempering_hold_min', 20);
  const ausTempLimitText = getProjectLimitText(projectLimitFields, 'austenitising_temp_c', `${ausTempMin} - ${ausTempMax} C`);
  const ausHoldLimitText = getProjectLimitText(projectLimitFields, 'austenitising_hold_min', `>= ${ausHoldMin} min`);
  const coolingRateLimitText = getProjectLimitText(projectLimitFields, 'cooling_rate_cps', `>= ${coolingRateMin} C/s`);
  const temperTempLimitText = getProjectLimitText(projectLimitFields, 'tempering_temp_c', `${temperTempMin} - ${temperTempMax} C`);
  const temperHoldLimitText = getProjectLimitText(projectLimitFields, 'tempering_hold_min', `>= ${temperHoldMin} min`);
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Austenitise', ausTempLimitText, true],
    ['Aus. hold', ausHoldLimitText, true],
    ['Cool rate', coolingRateLimitText, true],
    ['Temper', temperTempLimitText, true],
    ['Temper hold', temperHoldLimitText, true],
  ]);
  const stages = [
    {
      key: 'aus-temp',
      label: 'Austenitising - temp',
      formula: ausTempLimitText,
      value: Number(activeCycle?.heatAustenitisingTempC || 0),
      unit: 'C',
      min: Math.max(0, ausTempMin * 0.8),
      max: Math.max(ausTempMax * 1.15, Number(activeCycle?.heatAustenitisingTempC || 0)),
      lo: ausTempMin,
      hi: ausTempMax,
    },
    {
      key: 'aus-hold',
      label: 'Austenitising - hold',
      formula: ausHoldLimitText,
      value: Number(activeCycle?.heatAustenitisingHoldMin || 0),
      unit: 'min',
      min: Math.max(0, ausHoldMin * 0.8),
      max: Math.max(ausHoldMin * 1.5, Number(activeCycle?.heatAustenitisingHoldMin || 0)),
      lo: ausHoldMin,
    },
    {
      key: 'cooling',
      label: 'Cooling rate',
      formula: coolingRateLimitText,
      value: Number(activeCycle?.heatCoolingRateCps || 0),
      unit: 'C/s',
      min: Math.max(0, coolingRateMin * 0.8),
      max: Math.max(coolingRateMin * 1.5, Number(activeCycle?.heatCoolingRateCps || 0)),
      lo: coolingRateMin,
    },
    {
      key: 'temper-temp',
      label: 'Tempering - temp',
      formula: temperTempLimitText,
      value: Number(activeCycle?.heatTemperingTempC || 0),
      unit: 'C',
      min: Math.max(0, temperTempMin * 0.8),
      max: Math.max(temperTempMax * 1.2, Number(activeCycle?.heatTemperingTempC || 0)),
      lo: temperTempMin,
      hi: temperTempMax,
    },
    {
      key: 'temper-hold',
      label: 'Tempering - hold',
      formula: temperHoldLimitText,
      value: Number(activeCycle?.heatTemperingHoldMin || 0),
      unit: 'min',
      min: Math.max(0, temperHoldMin * 0.8),
      max: Math.max(temperHoldMin * 1.5, Number(activeCycle?.heatTemperingHoldMin || 0)),
      lo: temperHoldMin,
    },
  ];
  const stageStatus = (stage: typeof stages[number]) => {
    if (!activeCycle) return 'PENDING_REVIEW';
    if (stage.lo != null && stage.value < stage.lo) return 'FAIL';
    if (stage.hi != null && stage.value > stage.hi) return 'FAIL';
    return 'PASS';
  };
  const overallStatus = !activeCycle ? 'PENDING_REVIEW' : stages.some((stage) => stageStatus(stage) === 'FAIL') ? 'FAIL' : 'PASS';
  const chart = [
    [0, 120],
    [18, 640],
    [32, 880],
    [46, stages[0].value],
    [70, stages[0].value],
    [78, 120],
    [83, 120],
    [95, 450],
    [103, stages[3].value - 20],
    [112, stages[3].value],
    [146, stages[3].value],
    [158, 430],
    [170, 230],
    [178, 140],
  ];
  const maxTemp = Math.max(ausTempMax, temperTempMax, ...stages.filter((stage) => stage.unit === 'C').map((stage) => stage.value), 100);
  const maxTime = chart[chart.length - 1][0];
  const points = chart.map(([time, temp]) => `${(time / maxTime) * 800},${200 - (temp / maxTemp) * 200}`).join(' ');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Furnace strip chart</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{metadata?.source ? formatLabel(metadata.source) : 'Heat treatment'} · batch {batchId} · {pipesInBatch || 0} pipes</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('HEAT_TREAT')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Cycle
          </Button>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <svg viewBox="0 0 800 230" className="h-[230px] w-full">
              {[0, 0.25, 0.5, 0.75, 1].map((mark) => (
                <line key={mark} x1="0" y1={200 * mark} x2="800" y2={200 * mark} stroke="#EEF0F3" />
              ))}
              <rect x="0" y={200 - (ausTempMax / maxTemp) * 200} width="800" height={((ausTempMax - ausTempMin) / maxTemp) * 200} fill="#FFF5D6" opacity="0.75" />
              <text x="8" y={200 - (ausTempMax / maxTemp) * 200 - 6} fontFamily="monospace" fontSize="10" fill="#8A6500">austenitising window {ausTempMin}-{ausTempMax} C</text>
              <rect x="0" y={200 - (temperTempMax / maxTemp) * 200} width="800" height={((temperTempMax - temperTempMin) / maxTemp) * 200} fill="#E7F3FF" opacity="0.9" />
              <text x="8" y={200 - (temperTempMax / maxTemp) * 200 - 6} fontFamily="monospace" fontSize="10" fill="#1E5F9A">tempering window {temperTempMin}-{temperTempMax} C</text>
              <polyline points={points} fill="none" stroke="#1F5B89" strokeWidth="2" strokeLinejoin="round" />
              <text x="0" y="218" fontFamily="monospace" fontSize="10" fill="#6B7280">0 min</text>
              <text x="800" y="218" textAnchor="end" fontFamily="monospace" fontSize="10" fill="#6B7280">{maxTime} min</text>
            </svg>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Stage results" />
            {stages.map((stage) => (
              <TensileResultRow
                key={stage.key}
                label={stage.label}
                formula={stage.formula}
                value={stage.value}
                unit={stage.unit}
                min={stage.min}
                max={stage.max}
                lo={stage.lo}
                hi={stage.hi}
                status={stageStatus(stage)}
              />
            ))}
          </CardContent>
        </Card>

        {/* <div>
          <SectionHead title="Batch coverage" subtitle={activeCycle ? `This heat-treat record covers ${pipesInBatch || 0} pipes in furnace batch ${batchId}` : 'No heat-treat cycle saved yet'} />
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-4">
              <div className="grid grid-cols-12 gap-1">
                {Array.from({ length: pipesInBatch }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => activeCycle && setActiveCycleId(activeCycle.id)}
                    title={`Pipe ${index + 1}`}
                    className={cn(
                      'h-5 rounded border',
                      activeCycle ? 'border-galvanizing-green/30 bg-[rgba(31,154,58,0.10)]' : 'border-[#D9DEE6] bg-page-bg'
                    )}
                  />
                ))}
              </div>
              <div className="mt-2 text-[11px] text-heritage-grey">
                The selected furnace batch controls the stage results and strip chart.
              </div>
            </CardContent>
          </Card>
        </div> */}

        {heatMeasurements.length > 0 && (
          <Card className="overflow-hidden border-[#E2E5EA]">
            <CardContent className="p-0">
              <SectionHead title="User-added cycles" />
              <table className="w-full text-left">
                <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                  <tr>
                    <th className="px-4 py-3">Batch</th>
                    <th className="px-4 py-3 text-right">Aus C</th>
                    <th className="px-4 py-3 text-right">Aus Hold</th>
                    <th className="px-4 py-3 text-right">Cool C/s</th>
                    <th className="px-4 py-3 text-right">Temper C</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                  {heatMeasurements.map((measurement) => (
                    <tr key={measurement.id} onClick={() => setActiveCycleId(measurement.id)} className={cn('cursor-pointer hover:bg-page-bg', activeCycle?.id === measurement.id && 'bg-[#E7F3FF]')}>
                      <td className="px-4 py-3 font-mono font-semibold">{measurement.heatBatchId || measurement.id}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.heatAustenitisingTempC}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.heatAustenitisingHoldMin}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.heatCoolingRateCps}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.heatTemperingTempC}</td>
                      <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Batch ID', batchId, true], ['Pipes in batch', pipesInBatch || '-', true], ['Status', <StatusBadge key="status" status={overallStatus} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
